import moment from 'moment';
import formatCurrency from "format-currency";
import invoiceTimeEntryModal from '../../../common/modals/InvoiceTimeEntryModal/invoiceTimeEntryModal.component';
import _ from "lodash";

const httpOptions = { withCredentials: true };

class InvoiceCreateController {

  constructor(toastr, $http, $rootScope, $stateParams, AuthenticationService, $uibModal, $state) {
    "ngInject";

    this.toastr = toastr;
    this.$uibModal = $uibModal;
    this.timeEntries = [];
    this.customers = [];
    this.invoice = {
      customer: "all",
      contract: "all",
      project: "select",
      invoiceDate: moment().toDate(),
      signedDate: moment().toDate()
    };

    this.$http = $http;
    this.$rootScope = $rootScope;
    this.timeEntries = [];
    this.$state = $state;
    this.calculateInvoicePeriod();
    this.user = AuthenticationService.user;

    this.uninvoicedEntries = [];
    this.step = 1;
    this.checkingBillableLabor = false;
    this.preparingInvoice = false;
    this.billableLaborChecked = false;
    this.hasBillableLabor = null;
    this.loadingCustomers = false;
    this.loadingContracts = false;
    this.loadingProjects = false;
    this.loadingLineItems = false;
    this.unfundedChargeCodeWarnings = [];

    this.loadEligibleCustomers();

    $http.get(this.formAdminApi('companies/mycompany'), httpOptions).then((success) => {
        this.invoice.company = success.data;
        this.companyLogoUri = this.formAdminApi(`companies/${this.invoice.company.id}/logo`)
      });

    $http.get(this.formAdminApi('invoices/invnumber/next'), httpOptions).then((success) => {
      this.invoice.invoiceNumber = success.data;
    });

    $http.get(this.formApi('users/me'), httpOptions).then(
        (response) => {
          this.user = response.data
          this.invoice.signerName = this.user.firstName +" "+ this.user.lastName;
          this.invoice.signerTitle = this.user.title;
          this.invoice.signerEmail = this.user.userName
          this.invoice.signerPhone = this.user.phone;
        },
        () => this.user = undefined
      );


  }

  formApi(endpoint) {
    return `${this.$rootScope.apiHost}/api/${endpoint}`;
  }

  formAdminApi(endpoint) {
    return this.formApi(`admin/${endpoint}`);
  }

  getPeriodQueryString() {
    if (!this.invoice.invoicePeriodStart || !this.invoice.invoicePeriodEnd) {
      return '';
    }

    const periodStart = moment(this.invoice.invoicePeriodStart).format('YYYY-MM-DD');
    const periodEnd = moment(this.invoice.invoicePeriodEnd).format('YYYY-MM-DD');
    return `periodStart=${periodStart}&periodEnd=${periodEnd}`;
  }

  appendPeriodQuery(url) {
    const periodQuery = this.getPeriodQueryString();
    if (!periodQuery) {
      return url;
    }

    return url + (url.indexOf('?') >= 0 ? '&' : '?') + periodQuery;
  }

  loadEligibleCustomers() {
    this.loadingCustomers = true;
    const url = this.appendPeriodQuery(this.formAdminApi('invoices/labor/eligible-customers'));

    return this.$http.get(url, httpOptions)
      .then((response) => {
        this.customers = response.data || [];
        this.loadingCustomers = false;
      })
      .catch(() => {
        this.customers = [];
        this.loadingCustomers = false;
      });
  }

  loadEligibleContracts(customerId) {
    this.loadingContracts = true;
    let url = this.formAdminApi('invoices/labor/eligible-contracts?customerId=' + customerId);
    url = this.appendPeriodQuery(url);

    return this.$http.get(url, httpOptions)
      .then((response) => {
        this.contracts = response.data || [];
        this.loadingContracts = false;
        return this.contracts;
      })
      .catch(() => {
        this.contracts = [];
        this.loadingContracts = false;
        return this.contracts;
      });
  }

  loadEligibleProjects(contractId) {
    this.loadingProjects = true;
    let url = this.formAdminApi('invoices/labor/eligible-projects?contractId=' + contractId);
    url = this.appendPeriodQuery(url);

    return this.$http.get(url, httpOptions)
      .then((response) => {
        this.projects = response.data || [];
        this.loadingProjects = false;
        return this.projects;
      })
      .catch(() => {
        this.projects = [];
        this.loadingProjects = false;
        return this.projects;
      });
  }

  validateProjectSelection(previousProject) {
    if (previousProject === 'all') {
      if (!this.projects.length) {
        this.invoice.project = 'select';
      }
      return;
    }

    if (!previousProject || previousProject === 'select') {
      return;
    }

    const stillEligible = this.projects.some((project) => project.id === previousProject.id);
    if (!stillEligible) {
      this.invoice.project = 'select';
    }
  }

  reloadEligibleSelectionsAfterPeriodChange() {
    if (!this.invoice.customer || this.invoice.customer === 'all') {
      return Promise.resolve();
    }

    const customerId = this.invoice.customer.id;
    const selectedContract = this.invoice.contract;
    const selectedProject = this.invoice.project;
    const contractId = selectedContract && selectedContract !== 'all' ? selectedContract.id : null;

    return this.loadEligibleCustomers().then(() => {
      const customerStillEligible = this.customers.some((customer) => customer.id === customerId);
      if (!customerStillEligible) {
        this.invoice.customer = 'all';
        this.invoice.contract = 'all';
        this.invoice.project = 'select';
        this.contracts = [];
        this.projects = [];
        this.resetBillableLaborCheck();
        return;
      }

      return this.loadEligibleContracts(customerId).then(() => {
        if (!contractId) {
          return;
        }

        const contractStillEligible = this.contracts.some((contract) => contract.id === contractId);
        if (!contractStillEligible) {
          this.invoice.contract = 'all';
          this.invoice.project = 'select';
          this.projects = [];
          this.resetBillableLaborCheck();
          return;
        }

        return this.loadEligibleProjects(contractId).then(() => {
          this.validateProjectSelection(selectedProject);
          this.resetBillableLaborCheck();
          this.scheduleBillableLaborCheck();
        });
      });
    });
  }

  buildAvailabilityUrl() {
    const params = [
      'customerId=' + this.invoice.customer.id,
      'contractId=' + this.invoice.contract.id,
      'periodStart=' + moment(this.invoice.invoicePeriodStart).format('YYYY-MM-DD'),
      'periodEnd=' + moment(this.invoice.invoicePeriodEnd).format('YYYY-MM-DD')
    ];

    if (this.invoice.project !== 'all' && this.invoice.project && this.invoice.project !== 'select') {
      params.push('projectId=' + this.invoice.project.id);
    }

    return this.formAdminApi('invoices/labor/availability?' + params.join('&'));
  }

  selectCustomer() {
    this.invoice.contract = 'all';
    this.invoice.project = 'select';
    this.contracts = [];
    this.projects = [];
    this.resetBillableLaborCheck();

    if (!this.invoice.customer || this.invoice.customer === 'all') {
      return;
    }

    this.loadEligibleContracts(this.invoice.customer.id);
  }

  selectContract() {
    this.invoice.project = 'select';
    this.projects = [];
    this.resetBillableLaborCheck();

    if (!this.invoice.contract || this.invoice.contract === 'all') {
      return;
    }

    this.loadEligibleProjects(this.invoice.contract.id);
  }

  onProjectChange() {
    this.resetBillableLaborCheck();

    if (!this.isProjectContextSelected()) {
      return;
    }

    if (!this.invoicePeriodDirty) {
      this.calculateInvoicePeriod();
    }

    this.calculateDueDate();
    this.scheduleBillableLaborCheck();
  }

  resetBillableLaborCheck() {
    this.billableLaborChecked = false;
    this.hasBillableLabor = null;
    this.checkingBillableLabor = false;
    this.invoice.invoiceLineItems = [];
    this.uninvoicedEntries = [];
    this.unfundedChargeCodeWarnings = [];
  }

  scheduleBillableLaborCheck() {
    if (this.step !== 1 || !this.isSetupComplete()) {
      this.resetBillableLaborCheck();
      return;
    }

    this.checkBillableLaborAvailability();
  }

  getProjectsForFetch(projectChoice) {
    if (projectChoice === 'all') {
      return this.projects || [];
    }

    return this.invoice.project && this.invoice.project !== 'select' ? [this.invoice.project] : [];
  }

  fetchLineItemsAndEntries(projectChoice) {
    const options = { withCredentials: true };
    const ipStart = moment(this.invoice.invoicePeriodStart).format('YYYY-MM-DD');
    const ipEnd = moment(this.invoice.invoicePeriodEnd).format('YYYY-MM-DD');
    const endDate = ipEnd;
    const projectsToLoad = this.getProjectsForFetch(projectChoice);

    if (!projectsToLoad.length) {
      return Promise.resolve({
        lineItems: [],
        uninvoicedEntries: []
      });
    }

    const lineItemPromises = projectsToLoad.map((project) =>
      this.$http.get(this.$rootScope.apiHost + '/api/admin/projects/' + project.id + '/invoice/lineitems/' + endDate, options)
        .then((response) => response.data || [])
    );

    const timeEntryPromises = projectsToLoad.map((project) =>
      this.$http.get(this.$rootScope.apiHost + '/api/admin/projects/' + project.id + '/timeentries/' + ipStart + '/' + ipEnd, { withCredentials: true })
        .then((response) => response.data || [])
    );

    return Promise.all([
      Promise.all(lineItemPromises),
      Promise.all(timeEntryPromises)
    ]).then(([lineItemGroups, timeEntryGroups]) => ({
      lineItems: lineItemGroups.reduce((items, group) => items.concat(group), []),
      uninvoicedEntries: timeEntryGroups.reduce((entries, group) => entries.concat(group), [])
    }));
  }

  fetchCumulativesForPeriod(projectChoice) {
    const options = { withCredentials: true };
    const projectsToLoad = this.getProjectsForFetch(projectChoice);

    if (!projectsToLoad.length) {
      return Promise.resolve({ cumulatives: [], isAllProjects: projectChoice === 'all' });
    }

    const cumulativePromises = projectsToLoad.map((project) =>
      this.$http.get(this.$rootScope.apiHost + '/api/admin/projects/' + project.id + '/invoice/cumulatives', options)
        .then((response) => response.data)
    );

    return Promise.all(cumulativePromises).then((cumulatives) => ({
      cumulatives,
      isAllProjects: projectChoice === 'all'
    }));
  }

  loadUnfundedChargeCodesForSetup() {
    const projectChoice = this.invoice.project === 'all' ? 'all' : 'single';
    const projectsToLoad = this.getProjectsForFetch(projectChoice);

    if (!projectsToLoad.length) {
      this.unfundedChargeCodeWarnings = [];
      return Promise.resolve();
    }

    const requests = projectsToLoad.map((project) =>
      this.$http.get(this.$rootScope.apiHost + '/api/admin/projects/' + project.id, httpOptions)
        .then((response) => {
          const unfundedChargeCodes = response.data.unfundedChargeCodes || [];

          if (!unfundedChargeCodes.length) {
            return null;
          }

          return {
            projectId: project.id,
            projectName: response.data.name || project.name,
            unfundedChargeCodes: unfundedChargeCodes
          };
        })
        .catch(() => null)
    );

    return Promise.all(requests).then((results) => {
      this.unfundedChargeCodeWarnings = results.filter((result) => result !== null);
    });
  }

  hasUnfundedChargeCodes() {
    return this.unfundedChargeCodeWarnings.length > 0;
  }

  getSelectedProjectIdForUnfundedWarning() {
    if (this.unfundedChargeCodeWarnings.length === 1) {
      return this.unfundedChargeCodeWarnings[0].projectId;
    }

    if (this.invoice.project && this.invoice.project !== 'all' && this.invoice.project !== 'select') {
      return this.invoice.project.id;
    }

    return null;
  }

  loadLineItemsForSetup() {
    if (!this.isSetupComplete() || !this.hasBillableLabor) {
      this.invoice.invoiceLineItems = [];
      this.uninvoicedEntries = [];
      this.unfundedChargeCodeWarnings = [];
      return Promise.resolve();
    }

    const projectChoice = this.invoice.project === 'all' ? 'all' : 'single';
    this.loadingLineItems = true;

    return this.fetchLineItemsAndEntries(projectChoice)
      .then((data) => {
        this.uninvoicedEntries = data.uninvoicedEntries;
        this.invoice.invoiceLineItems = data.lineItems;
        return this.loadUnfundedChargeCodesForSetup();
      })
      .catch(() => {
        this.invoice.invoiceLineItems = [];
        this.uninvoicedEntries = [];
        this.unfundedChargeCodeWarnings = [];
      })
      .finally(() => {
        this.loadingLineItems = false;
      });
  }

  applyCumulativeData(data) {
    if (!data || !data.cumulatives) {
      return;
    }

    if (data.isAllProjects) {
      this.invoice.project = {
        id: 0,
        funding: 0
      };
      this.invoice.totalBilledToDate = 0;

      data.cumulatives.forEach((cumulative) => {
        if (!cumulative) {
          return;
        }

        this.invoice.project.funding += cumulative.funding || 0;
        this.invoice.totalBilledToDate += cumulative.totalInvoiced || 0;
      });
    } else if (data.cumulatives[0] && this.invoice.project && this.invoice.project !== 'all') {
      this.invoice.project.funding = data.cumulatives[0].funding;
      this.invoice.totalBilledToDate = data.cumulatives[0].totalInvoiced;
    }
  }

  fetchInvoiceDataForPeriod(projectChoice) {
    const options = { withCredentials: true };
    const ipStart = moment(this.invoice.invoicePeriodStart).format('YYYY-MM-DD');
    const ipEnd = moment(this.invoice.invoicePeriodEnd).format('YYYY-MM-DD');
    const endDate = ipEnd;
    const projectsToLoad = this.getProjectsForFetch(projectChoice);
    const isAllProjects = projectChoice === 'all';

    if (!projectsToLoad.length) {
      return Promise.resolve({
        lineItems: [],
        uninvoicedEntries: [],
        cumulatives: [],
        isAllProjects
      });
    }

    const lineItemPromises = projectsToLoad.map((project) =>
      this.$http.get(this.$rootScope.apiHost + '/api/admin/projects/' + project.id + '/invoice/lineitems/' + endDate, options)
        .then((response) => response.data || [])
    );

    const timeEntryPromises = projectsToLoad.map((project) =>
      this.$http.get(this.$rootScope.apiHost + '/api/admin/projects/' + project.id + '/timeentries/' + ipStart + '/' + ipEnd, { withCredentials: true })
        .then((response) => response.data || [])
    );

    const cumulativePromises = projectsToLoad.map((project) =>
      this.$http.get(this.$rootScope.apiHost + '/api/admin/projects/' + project.id + '/invoice/cumulatives', options)
        .then((response) => response.data)
    );

    return Promise.all([
      Promise.all(lineItemPromises),
      Promise.all(timeEntryPromises),
      Promise.all(cumulativePromises)
    ]).then(([lineItemGroups, timeEntryGroups, cumulatives]) => ({
      lineItems: lineItemGroups.reduce((items, group) => items.concat(group), []),
      uninvoicedEntries: timeEntryGroups.reduce((entries, group) => entries.concat(group), []),
      cumulatives,
      isAllProjects
    }));
  }

  hasInvoiceableLabor(data) {
    if (typeof data.hasBillableLabor === 'boolean') {
      return data.hasBillableLabor;
    }

    return data.lineItems.length > 0 && data.uninvoicedEntries.length > 0;
  }

  checkBillableLaborAvailability() {
    if (!this.isSetupComplete()) {
      this.resetBillableLaborCheck();
      return Promise.resolve(false);
    }

    this.checkingBillableLabor = true;

    return this.$http.get(this.buildAvailabilityUrl(), httpOptions)
      .then((response) => {
        this.checkingBillableLabor = false;
        this.billableLaborChecked = true;
        this.hasBillableLabor = this.hasInvoiceableLabor(response.data || {});

        if (this.hasBillableLabor) {
          return this.loadLineItemsForSetup();
        }

        this.invoice.invoiceLineItems = [];
        this.uninvoicedEntries = [];
        return this.hasBillableLabor;
      })
      .catch(() => {
        this.checkingBillableLabor = false;
        this.billableLaborChecked = true;
        this.hasBillableLabor = false;
        return false;
      });
  }

  applyInvoicePreviewData(data) {
    this.uninvoicedEntries = data.uninvoicedEntries;
    this.invoice.invoiceLineItems = data.lineItems;

    if (data.isAllProjects) {
      this.invoice.project = {
        id: 0,
        funding: 0
      };
      this.invoice.totalBilledToDate = 0;

      data.cumulatives.forEach((cumulative) => {
        this.invoice.project.funding += cumulative.funding || 0;
        this.invoice.totalBilledToDate += cumulative.totalInvoiced || 0;
      });
    } else if (data.cumulatives[0]) {
      this.invoice.project.funding = data.cumulatives[0].funding;
      this.invoice.totalBilledToDate = data.cumulatives[0].totalInvoiced;
    }
  }

  canProceedToPreview() {
    return this.isSetupComplete()
      && this.billableLaborChecked
      && this.hasBillableLabor
      && !this.checkingBillableLabor
      && !this.loadingLineItems
      && this.invoice.invoiceLineItems
      && this.getDisplayableLineItems().length > 0
      && this.getTotalHoursWorked() > 0;
  }

  isProjectContextSelected() {
    return this.invoice.customer && this.invoice.customer !== 'all'
      && this.invoice.contract && this.invoice.contract !== 'all'
      && (this.invoice.project === 'all'
        || (this.invoice.project && this.invoice.project !== 'select'));
  }

  isSetupComplete() {
    return this.isProjectContextSelected()
      && this.invoice.invoiceDate
      && this.invoice.invoicePeriodStart
      && this.invoice.invoicePeriodEnd
      && this.invoice.dueDate;
  }

  prepareInvoice() {
    if (!this.canProceedToPreview()) {
      return;
    }

    this.selectedProjectChoice = this.invoice.project;
    this.invoice.certification = this.invoice.contract && this.invoice.contract.invoiceStatement;
    this.step = 2;

    const projectChoice = this.selectedProjectChoice === 'all' ? 'all' : 'single';

    this.fetchCumulativesForPeriod(projectChoice)
      .then((data) => {
        this.applyCumulativeData(data);
      })
      .catch(() => {
        // Cumulative billing data is optional for the preview step.
      });
  }

  goBackToSetup() {
    this.step = 1;

    if (this.invoice.project && this.invoice.project.id === 0) {
      this.invoice.project = this.selectedProjectChoice || 'all';
    }
  }


  getActiveProjects(projects) {
    let active = [];

    if(projects) {
      projects.forEach((project) => {
        if (!project.closed) {
          active.push(project);
        }
      });
    }

    return active;
  }

  getNextInvoice() {
    const alpha = Math.random().toString(36).replace(/[0-9]/g,'').slice(-2).toUpperCase();
    const numeric = (Math.floor(Math.random()*1000000) % 100000).toString().padStart(5,'0')
    this.invoice.invoiceNumber = `${alpha}${numeric}`
  }

  getTimeEntriesForProject() {
    if(this.invoice.project != 'all') {
      const timeEntriesEndpoint = `projects/${this.invoice.project.id}/timeentries`;
      this.$http.get(this.formAdminApi(timeEntriesEndpoint), httpOptions)
        .then((success) => {
          this.timeEntries = success.data;
        });
    }
  }




  entryCodeComparator(a,b) {
    if(a.entryCode > b.entryCode) {
      return 1;
    }
    if(a.entryCode < b.entryCode) {
      return -1;
    }
    return 0;
  }



  currency(value) {
    return formatCurrency(value);
  }

  handlePreviewInvoiceDateChange() {
    this.handleInvoiceDateChange();

    if (!this.dueDateDirty) {
      this.calculateDueDate();
    }
  }

  handlePreviewInvoicePeriodChange() {
    this.handleInvoicePeriodChange();
    this.loadInvoiceDataForPeriod();
  }

  handleInvoiceDateChange() {
    if (!this.invoicePeriodDirty) {
      this.calculateInvoicePeriod();
    }

    if (this.step === 1) {
      this.reloadEligibleSelectionsAfterPeriodChange();
    }
  }

  handleDueDateChange() {
    this.dueDateDirty = true;
  }

  handleInvoicePeriodChange() {
    this.invoicePeriodDirty = true;

    if (this.step === 1) {
      this.reloadEligibleSelectionsAfterPeriodChange();
    }
  }

  handleContractPeriodChange() {
    this.contractPeriodDirty = true;
  }

  handlePurchaseOrderNumber() {
    this.purchaseOrderDirty = true;
  }

  calculateDueDate() {
    if(this.invoice.contract && this.invoice.contract !== 'all') {
      let netDue = 30;
      if(this.invoice.contract.netPaymentTerms){
        netDue = this.invoice.contract.netPaymentTerms;
      }

      this.invoice.dueDate = moment(this.invoice.invoiceDate).add(netDue, 'days').toDate();
    }
  }

  getContractPaymentTermsLabel() {
    if (!this.invoice.contract || this.invoice.contract === 'all') {
      return null;
    }

    const netDue = this.invoice.contract.netPaymentTerms || 30;
    return 'Net ' + netDue;
  }

  calculateInvoicePeriod() {
    if (this.invoice.invoiceDate) {
      this.invoice.signedDate = moment().toDate();
      this.invoice.signedDate.setHours(0, 0, 0, 0);

      const priorMonth = moment(this.invoice.invoiceDate).subtract(1, 'month');

      this.invoice.invoicePeriodStart = priorMonth.clone().startOf('month').toDate();
      this.invoice.invoicePeriodStart.setHours(0, 0, 0, 0);

      this.invoice.invoicePeriodEnd = priorMonth.clone().endOf('month').toDate();
      this.invoice.invoicePeriodEnd.setHours(0, 0, 0, 0);
    }
  }

  getTotalHoursWorked() {
    let total = 0;
    if(this.invoice && this.invoice.invoiceLineItems) {
      this.invoice.invoiceLineItems.forEach((li) => {
        total += this.getHoursWorkedForLineItem(li);
      });
    }

    return total;
  }

  getTotalCumulativeHoursWorked() {
    let total = 0;
    if(this.invoice && this.invoice.invoiceLineItems) {
      this.invoice.invoiceLineItems.forEach((li) => {
        total += li.cumulativeHoursWorked;
        total += this.getHoursWorkedForLineItem(li);
      });
    }

    return total;
  }

  getTotalCumulativeDollars() {
    let total = 0;
    if(this.invoice && this.invoice.invoiceLineItems) {
      this.invoice.invoiceLineItems.forEach((li) => {
        total += li.cumulativeDollars;
        total += this.getCurrentDollarsForLineItem(li);
      });
    }

    return total;
  }

  getTotalDollars() {
    let total = 0;
    if(this.invoice && this.invoice.invoiceLineItems) {
      this.invoice.invoiceLineItems.forEach((li) => {
        total += this.getCurrentDollarsForLineItem(li);
      });
    }

    return total;
  }

  getLineItemsForPreview() {
    return this.invoice.invoiceLineItems || [];
  }

  getPreviewProject() {
    if (this.invoice.project && this.invoice.project.id === 0) {
      if (this.selectedProjectChoice === 'all') {
        return { name: 'All Projects' };
      }

      return this.selectedProjectChoice;
    }

    return this.invoice.project;
  }

  getCumulativeHoursForLineItem(lineItem) {
    return (lineItem.cumulativeHoursWorked || 0) + this.getHoursWorkedForLineItem(lineItem);
  }

  getCumulativeDollarsForLineItem(lineItem) {
    return (lineItem.cumulativeDollars || 0) + this.getCurrentDollarsForLineItem(lineItem);
  }

  getPreviewTotalCumulativeHoursWorked() {
    let total = 0;
    this.getLineItemsForPreview().forEach((lineItem) => {
      total += this.getCumulativeHoursForLineItem(lineItem);
    });

    return total;
  }

  getPreviewTotalCumulativeDollars() {
    let total = 0;
    this.getLineItemsForPreview().forEach((lineItem) => {
      total += this.getCumulativeDollarsForLineItem(lineItem);
    });

    return total;
  }

  loadInvoiceDataForPeriod() {
    const projectChoice = this.selectedProjectChoice === 'all' ? 'all' : 'single';

    this.loadingLineItems = true;
    this.fetchLineItemsAndEntries(projectChoice).then((data) => {
      this.uninvoicedEntries = data.uninvoicedEntries;
      this.invoice.invoiceLineItems = data.lineItems;
      this.loadingLineItems = false;
    }).catch(() => {
      this.loadingLineItems = false;
    });
  }

  addLineItemToInvoice(lineItem) {
    let self = this;
    var modalInstance = this.$uibModal.open(invoiceTimeEntryModal);
    modalInstance.invoice = this.invoice;
    modalInstance.userId = lineItem.user.id;
    modalInstance.chargeCodeId = lineItem.chargeCode.id;
    modalInstance.employee = lineItem.user.firstName + " " + lineItem.user.lastName;
    modalInstance.chargeCode = lineItem.chargeCode.name;
    modalInstance.lineItem = lineItem;
    modalInstance.uninvoicedEntries = this.uninvoicedEntries;

    modalInstance.success = function () {
      modalInstance.dismiss();
    };

    modalInstance.cancel = function () {
      modalInstance.dismiss();
    };

  }

  getMatchableUninvoicedEntriesForLineItem(lineItem) {
    if (!this.uninvoicedEntries || !lineItem || !lineItem.user || !lineItem.chargeCode) {
      return [];
    }

    return _.filter(this.uninvoicedEntries, (entry) => {
      return entry.employeeId == lineItem.user.id
        && entry.chargeCode.id == lineItem.chargeCode.id
        && moment(entry.date).isSameOrAfter(this.invoice.invoicePeriodStart)
        && moment(entry.date).isSameOrBefore(this.invoice.invoicePeriodEnd);
    });
  }

  lineItemHasAvailableHours(lineItem) {
    return this.getMatchableUninvoicedEntriesForLineItem(lineItem).length > 0
      || this.getHoursWorkedForLineItem(lineItem) > 0;
  }

  getDisplayableLineItems() {
    if (!this.invoice.invoiceLineItems) {
      return [];
    }

    return this.invoice.invoiceLineItems.filter((lineItem) => this.lineItemHasAvailableHours(lineItem));
  }

  getHoursWorkedForLineItem(lineItem) {
    let hours = 0.00;
    if(lineItem.timeEntries) {
      lineItem.timeEntries.forEach((entry) => {
        hours += entry.hours;
      });
    }

    return hours;
  }

  getCurrentDollarsForLineItem(lineItem) {
    return this.getHoursWorkedForLineItem(lineItem) * lineItem.rate;
  }

  createInvoice() {
    let invoiceToSend = this.invoice;

    invoiceToSend.customer = {id: this.invoice.customer.id};
    invoiceToSend.project = {id: this.invoice.project.id};
    invoiceToSend.contract = {id: this.invoice.contract.id};
    invoiceToSend.company = {};

    let lineItems = [];

    this.invoice.invoiceLineItems.forEach((lineItem) => {
      let lineItemToSend = {};
      lineItemToSend.lineNumber = lineItem.lineNumber;
      lineItemToSend.chargeCode = {id: lineItem.chargeCode.id};
      lineItemToSend.laborCategory = {id: lineItem.laborCategory.id};
      lineItemToSend.user = {id: lineItem.user.id};

      if(lineItem.timeEntries != null) {
        lineItemToSend.timeEntries = [];
        lineItem.timeEntries.forEach((timeEntry) => {
          timeEntry = {id: timeEntry.id}
          lineItemToSend.timeEntries.push(timeEntry);
        });
      }

      lineItems.push(lineItemToSend);
    });

    invoiceToSend.invoiceLineItems = lineItems;


    this.$http.post(this.$rootScope.apiHost + '/api/admin/invoices', invoiceToSend, { withCredentials: true }).then((success) => {
      this.$state.go('admin.invoice-details', {invoiceId: success.data.id})
    });
  }

  validateInvoice() {
    let invalid = true;

    if (this.invoice.invoiceNumber && this.invoice.invoiceDate && this.invoice.invoicePeriodStart && this.invoice.invoicePeriodEnd && this.invoice.project && this.invoice.signedDate && this.getTotalHoursWorked() > 0) {
      invalid = false;
    }

    return invalid;
  }

  clearLineItemHours(lineItem) {
    lineItem.timeEntries = [];
  }

  removeLineItem(lineItem){
    _.remove(this.invoice.invoiceLineItems, function (item) {
      return lineItem.lineNumber === item.lineNumber;
    });

    let i = 1;
    this.invoice.invoiceLineItems.forEach(function (li) {
      li.lineNumber = i;
      i++;
    });



  }

  addAllHours(){
      this.getDisplayableLineItems().forEach((lineItem) => {
        let entriesForEmployee = this.getMatchableUninvoicedEntriesForLineItem(lineItem);

        if(lineItem.timeEntries) {
          lineItem.timeEntries.forEach((entry) => {
            entriesForEmployee.forEach((obj) => {
              let x = obj.date == entry.date && obj.hours == entry.hours && obj.chargeCode.id == entry.chargeCode.id;
              if(x) {
                obj.selected = true;
              }
            });
          });
        }

        lineItem.timeEntries = entriesForEmployee;
      });

  }


}

export default InvoiceCreateController;



// WEBPACK FOOTER //
// ./client/pages/admin/invoice-create/invoice-create.controller.js